import {
  Form,
  Input,
  Button,
  Space,
  Breadcrumb,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { requiredRule } from 'utils/forms/validationRules';
import { RolesService } from 'services/RolesService';
import { TableSearchPanel } from 'ui/TableSearchPanel';
import { REQUEST_LOADING } from 'constants/alerts';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { GroupsService } from 'services/GroupsService';
import { COLUMN_PRE_LARGE, LABEL_SPAN } from 'constants/table';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

function RolesFormPage({ formTitle }) {
  useDocumentTitle(formTitle);
  useHeaderChanger(formTitle);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [pages, setPages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroups, setActiveGroups] = useState([]);
  const [role, setRole] = useState(null);
  const { id: roleId } = useParams();

  const isEditing = formTitle === 'Редактирование роли';

  useEffect(() => {
    fetchGroups();

    if (!isEditing) {
      fetchPages();
    } else {
      fetchCurrentRoleData();
    }
  }, []);

  useEffect(() => {
    if (role) {
      const {
        active,
        description,
        name,
        permissions,
        slug,
        allClient,
        groups,
      } = role;

      form.setFieldsValue({
        active,
        description,
        name,
        slug,
        allClient,
      });

      setActiveGroups(groups);
      setPages(permissions);
    }
  }, [role]);

  const fetchPages = async () => {
    try {
      const data = await RolesService.getPages();
      setPages(data);
    } catch (err) {
      message.error('Не удалось загрузить страницы');
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await GroupsService.getAllGroups();
      setGroups(data);
    } catch (err) {
      message.error('Не удалось загрузить группы');
    }
  };

  const fetchCurrentRoleData = async () => {
    try {
      const [pages, role] = await Promise.all([
        RolesService.getPages(),
        RolesService.getRole(roleId),
      ]);

      const data = updateRolePermissions(pages, role);
      setRole(data);
    } catch (err) {
      message.error('Не удалось загрузить информацию о роли');
    }
  };

  const updateRolePermissions = (pages, role) => {
    const currentRole = { ...role };

    const updatedPages = pages.map((page) => {
      const { slug } = page;
      const rolePage = role.permissions.find((item) => item.slug === slug);

      if (rolePage) {
        const { create, update, read } = rolePage;
        page.create = create;
        page.read = read;
        page.update = update;
        page.delete = rolePage.delete;
      }

      return page;
    });

    currentRole.permissions = [...updatedPages];

    return currentRole;
  };

  const handleCreateRole = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await RolesService.createRole(payload);

      if (isCreated) {
        hide();
        message.success('Роль успешно создана');
        navigate('/setting/access-manage/roles');
      }
    } catch (err) {
      message.error('Не удалось создать роль');
    }
  };

  const handleEditRole = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await RolesService.editRole(payload, roleId);

      if (isUpdated) {
        hide();
        message.success('Роль успешно отредактирована');
        navigate('/setting/access-manage/roles');
      }
    } catch (err) {
      message.error('Не удалось отредактировать роль');
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      permissions: pages,
      groups: activeGroups,
    };

    if (isEditing) {
      handleEditRole(payload);
    } else {
      handleCreateRole(payload);
    }
  };

  const handleAccessChange = (record, action) => (value) => {
    const currentPageIdx = pages.findIndex((item) => item.slug === record.slug);
    const items = [...pages];
    items[currentPageIdx][action] = value;
    setPages(items);
  };

  const handleAccessToggle = (record) => (value) => {
    const currentPageIdx = pages.findIndex((item) => item.slug === record.slug);
    const items = [...pages];

    items[currentPageIdx].create = value;
    items[currentPageIdx].update = value;
    items[currentPageIdx].read = value;
    items[currentPageIdx].delete = value;

    setPages(items);
  };

  const handleGroupAccess = (id) => {
    const isActive = activeGroups.find((item) => item === id);
    const result = activeGroups.filter((item) => item !== id);

    if (isActive) {
      setActiveGroups(result);
    } else {
      setActiveGroups([...activeGroups, id]);
    }
  };

  const roleColumns = [
    {
      title: 'Название страницы',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ selectedKeys, setSelectedKeys, confirm }) => {
        return (
          <TableSearchPanel>
            <Input
              autoFocus
              placeholder="Поиск по названию страницы"
              value={selectedKeys[0]}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
                confirm({ closeDropdown: false });
              }}
              onPressEnter={() => confirm()}
            />
          </TableSearchPanel>
        );
      },
      filterIcon: () => <SearchOutlined />,
      onFilter: (value, record) => {
        return record?.name.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: 'Модуль',
      dataIndex: 'module',
      key: 'module',
      render: (module) => <Tag>{module}</Tag>,
    },
    {
      title: 'CREATE',
      dataIndex: 'create',
      key: 'create',
      render: (_, record) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={record.create}
            onChange={handleAccessChange(record, 'create')}
          />
        );
      },
      width: 100,
    },
    {
      title: 'READ',
      dataIndex: 'read',
      key: 'read',
      render: (_, record) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={record.read}
            onChange={handleAccessChange(record, 'read')}
          />
        );
      },
      width: 100,
    },
    {
      title: 'UPDATE',
      dataIndex: 'update',
      key: 'update',
      render: (_, record) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={record.update}
            onChange={handleAccessChange(record, 'update')}
          />
        );
      },
      width: 100,
    },
    {
      title: 'DELETE',
      dataIndex: 'delete',
      key: 'delete',
      render: (_, record) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={record.delete}
            onChange={handleAccessChange(record, 'delete')}
          />
        );
      },
      width: 100,
    },
    {
      width: COLUMN_PRE_LARGE,
      title: 'Доступ ко всем операциям',
      key: 'access',
      align: 'center',
      render: (_, record) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={
              record.delete && record.update && record.read && record.create
            }
            onChange={handleAccessToggle(record)}
          />
        );
      },
    },
  ];

  const groupsColumns = [
    {
      title: 'Группа пользователей',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Доступ',
      dataIndex: 'id',
      key: 'id',
      render: (value) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={activeGroups.includes(value)}
            onChange={() => handleGroupAccess(value)}
          />
        );
      },
      width: 100,
    },
  ];

  return (
    <>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        labelAlign="left"
        initialValues={{
          active: true,
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Breadcrumb>
            <Breadcrumb.Item>СУД</Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/setting/access-manage/roles">Роли</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{formTitle}</Breadcrumb.Item>
          </Breadcrumb>

          <Space align="center" size="small">
            <Form.Item>
              <Button
                type="default"
                htmlType="button"
                onClick={() => navigate(-1)}
              >
                Отменить
              </Button>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
            </Form.Item>
          </Space>
        </div>

        <div style={{ width: '50%' }}>
          <Form.Item name="name" label="Наименование" rules={[requiredRule]}>
            <Input placeholder="Введите наименование роли" allowClear />
          </Form.Item>

          <Form.Item name="description" label="Описание" rules={[requiredRule]}>
            <Input placeholder="Введите описание" allowClear />
          </Form.Item>

          <Form.Item name="slug" label="Код" rules={[requiredRule]}>
            <Input placeholder="Введите код роли" allowClear />
          </Form.Item>

          <Form.Item name="active" label="Статус" valuePropName="checked">
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="allClient"
            label="Доступ ко всем клиентам"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
        </div>
      </Form>

      <Table
        columns={roleColumns}
        dataSource={pages}
        rowKey="slug"
        pagination={false}
        scroll={{ y: 340 }}
      />

      <Table
        style={{ marginTop: '20px' }}
        columns={groupsColumns}
        dataSource={groups}
        rowKey="slug"
        pagination={false}
        scroll={{ y: 340 }}
      />
    </>
  );
}

export { RolesFormPage };
